/**
 * User data service for normalizing and transforming user data
 * between frontend (camelCase) and backend (snake_case) formats
 */
import { normalizeUser, toSnakeCase, getMappedUserField } from '../utils/userDataNormalizer';

/**
 * Normalize user data from backend response
 * Handles both camelCase and snake_case formats
 * @param {Object} userData - User data from API
 * @returns {Object} - Normalized user object in camelCase
 */
export const normalizeUserData = (userData) => {
  return normalizeUser(userData);
};

/**
 * Transform user data for backend API (convert to snake_case)
 * @param {Object} userData - User data in camelCase
 * @returns {Object} - User data in snake_case for API
 */
export const transformForBackend = (userData) => {
  return toSnakeCase(userData);
};

/**
 * Get user field value handling both formats
 * @param {Object} user - User object
 * @param {string} fieldName - Field name in camelCase
 * @returns {*} - Field value
 */
export const getUserFieldValue = (user, fieldName) => {
  return getMappedUserField(user, fieldName);
};

/**
 * Get all user fields in a normalized format
 * @param {Object} user - User object
 * @returns {Object} - Normalized user object
 */
export const getUserFields = (user) => {
  if (!user) return {};
  
  const normalized = normalizeUser(user);
  
  return {
    // Personal info
    id: normalized.id,
    name: normalized.name,
    email: normalized.email,
    phone: normalized.phone || '',
    bloodType: normalized.bloodType || normalized.blood_type || '',
    gender: normalized.gender || '',
    birthYear: normalized.birthYear || normalized.birth_year || null,
    
    // Location
    country: normalized.country || '',
    city: normalized.city || '',
    state: normalized.state || '',
    zipCode: normalized.zipCode || normalized.zip_code || '',
    address: normalized.address || '',
    
    // Donation preferences
    isBloodDonor: normalized.isBloodDonor || normalized.is_blood_donor || false,
    isOrganDonor: normalized.isOrganDonor || normalized.is_organ_donor || false,
    organsPledged: normalized.organsPledged || normalized.organs_pledged || [],
    bodyDonation: normalized.bodyDonation || normalized.body_donation || false,
    donationType: normalized.donationType || normalized.donation_type || null,
    
    // Emergency contact
    emergencyContact: normalized.emergencyContact || normalized.emergency_contact || null,
    
    // Other
    profilePicture: normalized.profilePicture || normalized.profile_picture || null,
    sosOptIn: normalized.sosOptIn || normalized.sos_opt_in || false,
  };
};

/**
 * Prepare user data for API update
 * Converts camelCase to snake_case and removes undefined/null values
 * @param {Object} userData - User data in camelCase
 * @returns {Object} - Cleaned user data in snake_case
 */
export const prepareForUpdate = (userData) => {
  const cleaned = {};
  
  for (const [key, value] of Object.entries(userData)) {
    // Skip undefined, but keep null (for clearing fields)
    if (value !== undefined) {
      cleaned[key] = value;
    }
  }
  
  return transformForBackend(cleaned);
};

/**
 * Calculate age from birth year
 * @param {number|null|undefined} birthYear - Birth year
 * @returns {number|null} - Age in years or null if birthYear is invalid
 */
export const calculateAge = (birthYear) => {
  if (!birthYear || typeof birthYear !== 'number') {
    return null;
  }
  
  const currentYear = new Date().getFullYear();
  const age = currentYear - birthYear;
  
  // Validate age is reasonable (between 0 and 150)
  if (age < 0 || age > 150) {
    return null;
  }
  
  return age;
};

